/**
 * @file 权限管理 Query Hook (核心数据获取)
 */

import {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {isAxiosError} from 'axios';
import {fetchPermissions} from '@/services/permissionService';
import {usePermissionsStore} from '@/store/usePermissionsStore';
import {useAuthStore} from '@/store/useAuthStore';
import {useProjectId} from '@/hooks/useProjectId';
import type {ProjectId} from '@/types';
import {ERROR_MESSAGES} from '@/constants';
import {queryClient} from '@/utils/queryClient';

/**
 * 权限查询 Key Factory
 * 遵循项目的 Key Factory Pattern 最佳实践
 */
export const permissionKeys = {
    all: ['permissions'] as const,
    project: (projectId: ProjectId) => [...permissionKeys.all, 'project', projectId] as const,
};

/**
 * 主动刷新指定项目的权限缓存
 * 在权限相关操作（下载权限修改、成员角色变更等）成功后调用，立即同步最新权限
 */
export const refreshProjectPermissions = (projectId: ProjectId): void => {
    if (!projectId) return;
    void queryClient.invalidateQueries({
        queryKey: permissionKeys.project(projectId),
    });
};

/**
 * 项目权限查询 Hook
 * @description
 * - 切换到不同项目：通过 React Query 管理缓存与请求
 * - 同一个项目再次进入：走缓存，5分钟内不重复发请求
 */
export const useProjectPermissions = () => {
    const projectId = useProjectId();

    const setPermissions = usePermissionsStore((state) => state.setPermissions);
    const setLoading = usePermissionsStore((state) => state.setLoading);
    const setError = usePermissionsStore((state) => state.setError);
    const clearPermissions = usePermissionsStore((state) => state.clearPermissions);
    const setCurrentProjectId = usePermissionsStore((state) => state.setCurrentProjectId);

    const isLogin = useAuthStore((state) => state.isLogin);

    const query = useQuery({
        queryKey: permissionKeys.project(projectId),
        queryFn: () => fetchPermissions({projectId}),
        enabled: isLogin && !!projectId,
        staleTime: 5 * 60 * 1000, // 5分钟缓存，避免频繁请求
    });

    // 单向数据流：只按 Query 的状态更新权限全局 Store
    useEffect(() => {
        // projectId 为空时清除所有项目权限
        if (!projectId) {
            clearPermissions();
            return;
        }

        // 立即向 Store 同步最新的项目 ID，防止切换项目等待接口响应期间，旧 ID 滞留
        setCurrentProjectId(projectId);

        // isPending：无缓存数据首次正在请求
        setLoading(query.isPending);

        // 错误状态
        if (query.isError) {
            const is403 = isAxiosError(query.error) && query.error.response?.status === 403;
            const msg = is403
                ? ERROR_MESSAGES.PERMISSION_NO_ACCESS
                : ERROR_MESSAGES.PERMISSION_FETCH_FAILED;
            setError(msg);

            return;
        }

        // 成功获取数据 (由于有 staleTime 设置，这里无论是 fetch 成功还是使用缓存阶段都能被触发)
        if (query.isSuccess && query.data) {
            setPermissions(projectId, query.data.permissionCodes, query.data.roleCodes);
        }
    }, [
        projectId,
        query.isPending,
        query.isError,
        query.data,
        setPermissions,
        setLoading,
        setError,
        clearPermissions,
        setCurrentProjectId,
        query.isSuccess,
        query.error,
    ]);

    return query;
};
